// Single typed accessor for build-time config on the booking surface.
// Everything else reads from here rather than touching import.meta.env, so a
// missing or crossed value fails loudly instead of surfacing as `undefined`
// deep inside a request.
//
// Every value here is PUBLIC by design — the Firebase web config and the App
// Check reCAPTCHA site key are embeddable. Secrets live in Infisical and must
// never be added to this surface's per-environment config.
//
// Public booking reads use /app. A credentialed client's own account is read
// from /core, derived from the validated same-environment /app URL below so a
// second environment variable cannot silently point at a different backend.

export type LuraEnv = "dev" | "qa" | "staging" | "prod";

function required(value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(
      `Missing build config ${name}. Builds must run with --mode <dev|qa|staging|prod> so the matching env file is loaded.`
    );
  }
  return value;
}

// "Environments must never cross" is a hosting contract, not a convention. A qa
// build talking to prod would attach the wrong App Check token and create real
// bookings against real professionals. Assert it at boot.
const EXPECTED_API_HOST: Record<LuraEnv, string> = {
  dev: "dev.api.uselura.com",
  qa: "qa.api.uselura.com",
  staging: "staging.api.uselura.com",
  prod: "api.uselura.com",
};

const EXPECTED_WEB_HOST: Record<LuraEnv, string> = {
  dev: "dev.book.uselura.com",
  qa: "qa.book.uselura.com",
  staging: "staging.book.uselura.com",
  prod: "book.uselura.com",
};

function assertHost(actual: string, expected: string, name: string, env: LuraEnv): string {
  // Compare parsed hostnames — "api.uselura.com" is a suffix of
  // "dev.api.uselura.com", so a substring check would pass a crossed env.
  if (actual !== expected) {
    throw new Error(
      `Environment mismatch: VITE_ENV=${env} expects ${name} on ${expected}, got ${actual}. ` +
        `Rebuild with the matching --mode, or fix this environment's config.`
    );
  }
  return actual;
}

function readEnv(): LuraEnv {
  const value = required(import.meta.env.VITE_ENV, "VITE_ENV");
  if (value !== "dev" && value !== "qa" && value !== "staging" && value !== "prod") {
    throw new Error(`VITE_ENV must be dev|qa|staging|prod, got "${value}".`);
  }
  return value;
}

const env = readEnv();

const appUrl = required(import.meta.env.VITE_GRAPHQL_APP_URL, "VITE_GRAPHQL_APP_URL");
assertHost(new URL(appUrl).hostname, EXPECTED_API_HOST[env], "VITE_GRAPHQL_APP_URL", env);
if (new URL(appUrl).pathname !== "/api/v1/app") {
  throw new Error("VITE_GRAPHQL_APP_URL must end at /api/v1/app.");
}
const coreUrl = new URL("/api/v1/core", appUrl).toString();

const webHost = required(import.meta.env.VITE_WEB_HOST, "VITE_WEB_HOST");
assertHost(webHost, EXPECTED_WEB_HOST[env], "VITE_WEB_HOST", env);

export const config = {
  env,
  // NOT named `isDev`. In apps/app, `isDev` means a LOCAL dev server and is
  // false on dev.app.uselura.com. This one means "the dev environment", which
  // is true there. Same short name, opposite meaning — and a pattern copied
  // between surfaces (`if (config.isDev) enableDebugToken()`) would silently
  // arm a local-only path on a deployed host.
  isDevEnvironment: env === "dev",
  /** The public /app GraphQL endpoint. The only API this surface may call. */
  appUrl,
  /** Authenticated client account reads; derived from the validated API origin. */
  coreUrl,
  /** This surface's own host, per environment. */
  webHost,
  firebase: {
    apiKey: required(import.meta.env.VITE_FIREBASE_API_KEY, "VITE_FIREBASE_API_KEY"),
    // Book's own host in every environment (validate-quality-contracts.js).
    // Provider controls are offered only where this equals webHost; deploying
    // still needs the authorized domain, provider callbacks, reCAPTCHA
    // allowance and connected browser proof. Not asserted here at runtime: a
    // mismatch withholds the buttons rather than taking down the surface.
    authDomain: required(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN, "VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: required(import.meta.env.VITE_FIREBASE_PROJECT_ID, "VITE_FIREBASE_PROJECT_ID"),
    storageBucket: required(
      import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      "VITE_FIREBASE_STORAGE_BUCKET"
    ),
    messagingSenderId: required(
      import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      "VITE_FIREBASE_MESSAGING_SENDER_ID"
    ),
    appId: required(import.meta.env.VITE_FIREBASE_APP_ID, "VITE_FIREBASE_APP_ID"),
  },
  /**
   * Local dev only, pulled from Infisical into a gitignored local override and
   * never committed. Read through config rather than at the use site so the
   * `isDevEnvironment` guard sits next to it and a deployed build cannot arm it.
   */
  appCheckDebugToken: import.meta.env.VITE_APPCHECK_DEBUG_TOKEN,
  appCheckSiteKey: required(
    import.meta.env.VITE_APPCHECK_RECAPTCHA_SITE_KEY,
    "VITE_APPCHECK_RECAPTCHA_SITE_KEY"
  ),
} as const;
